import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { employeesApi } from '../../services/employees';
import { assetsApi } from '../../services/assets';
import { employeeTimeAllocationApi } from '../../services/employeeTimeAllocation';
import { useAuth } from '../../context/AuthContext';

function EmployeeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasRole, user } = useAuth();
  const [employee, setEmployee] = useState(null);
  const [assets, setAssets] = useState([]);
  const [timeAllocationHistory, setTimeAllocationHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showTimeAllocationModal, setShowTimeAllocationModal] = useState(false);

  const [timeAllocationFormData, setTimeAllocationFormData] = useState({
    weekStartDate: '',
    timeAllocationPercentage: '',
    notes: ''
  });

  // Helper function to get Friday of a given date
  const getFridayOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -2 : 5); // Adjust to Friday (5)
    const friday = new Date(d.setDate(diff));
    return friday.toISOString().split('T')[0];
  };

  // Helper function to get week range (Monday to Sunday)
  const getWeekRange = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const monday = new Date(d);
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    monday.setDate(diff);
    
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    
    return {
      start: monday,
      end: sunday,
      friday: getFridayOfWeek(monday)
    };
  };

  // Generate week options (last week, this week)
  const getWeekOptions = () => {
    const today = new Date();
    const options = [];

    // Last week
    const lastWeek = new Date(today);
    lastWeek.setDate(today.getDate() - 7);
    const lastWeekRange = getWeekRange(lastWeek);
    options.push({
      value: lastWeekRange.friday,
      label: `Last Week (${lastWeekRange.start.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} - ${lastWeekRange.end.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })})`
    });

    // This week
    const thisWeekRange = getWeekRange(today);
    options.push({
      value: thisWeekRange.friday,
      label: `This Week (${thisWeekRange.start.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} - ${thisWeekRange.end.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })})`
    });

    return options;
  };

  const weekOptions = getWeekOptions();

  // Helper function to format week range for display (stored date is Friday)
  const formatWeekRange = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDay();
    const monday = new Date(date);
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    monday.setDate(diff);
    
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    
    return `${monday.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} - ${sunday.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`;
  };

  const fetchEmployee = useCallback(async () => {
    try {
      const data = await employeesApi.getById(id);
      setEmployee(data);
    } catch (err) {
      setError('Failed to load employee details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchEmployeeAssets = useCallback(async () => {
    try {
      const response = await assetsApi.searchByEmployee(id);
      const data = response.data || response;
      console.log('Employee assets:', data);
      setAssets(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load employee assets');
      setAssets([]);
    }
  }, [id]);

  const fetchTimeAllocationHistory = useCallback(async () => {
    try {
      const data = await employeeTimeAllocationApi.getByEmployee(id);
      setTimeAllocationHistory(data);
    } catch (err) {
      console.error('Failed to load time allocation history');
      setTimeAllocationHistory([]);
    }
  }, [id]);

  useEffect(() => {
    fetchEmployee();
    fetchEmployeeAssets();
    fetchTimeAllocationHistory();
  }, [fetchEmployee, fetchEmployeeAssets, fetchTimeAllocationHistory]);

  const handleTimeAllocationFormChange = (e) => {
    const { name, value } = e.target;
    setTimeAllocationFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCreateTimeAllocation = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await employeeTimeAllocationApi.create({
        employeeId: id,
        weekStartDate: timeAllocationFormData.weekStartDate,
        timeAllocationPercentage: timeAllocationFormData.timeAllocationPercentage,
        notes: timeAllocationFormData.notes
      });
      setShowTimeAllocationModal(false);
      setTimeAllocationFormData({
        weekStartDate: '',
        timeAllocationPercentage: '',
        notes: ''
      });
      fetchTimeAllocationHistory();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save time allocation');
    }
  };

  const handleDeleteTimeAllocation = async (timeAllocationId) => {
    if (!window.confirm('Are you sure you want to delete this time allocation entry?')) return;
    try {
      await employeeTimeAllocationApi.delete(timeAllocationId);
      fetchTimeAllocationHistory();
    } catch (err) {
      setError('Failed to delete time allocation entry');
    }
  };

  const getStatusBadge = (status) => {
    const classes = {
      active: 'badge badge-active',
      inactive: 'badge badge-inactive',
      exited: 'badge badge-rejected'
    };
    return <span className={classes[status] || 'badge'}>{status.replace('_', ' ')}</span>;
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (error || !employee) {
    return (
      <div className="card" style={{ backgroundColor: '#ffebee', color: '#c62828' }}>
        {error || 'Employee not found'}
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h2>Employee Profile</h2>
        <div style={{ display: 'flex', gap: '12px' }}>
          {hasRole('hr_head', 'super_admin') ? (
            <>
              <Link to={`/employees/${id}/edit`} className="btn btn-primary btn-compact">
                Edit
              </Link>
              <Link to={`/assets/new?employee_id=${id}`} className="btn btn-primary btn-compact">
                + Assign Asset
              </Link>
              {user.id === id && (
                <button
                  className="btn btn-secondary btn-compact"
                  onClick={() => setShowTimeAllocationModal(true)}
                >
                  + Add Time Allocation
                </button>
              )}
              {user.id === id ? (
                <Link to="/dashboard" className="btn btn-secondary btn-compact">
                  Back to Dashboard
                </Link>
              ) : (
                <Link to="/employees" className="btn btn-secondary btn-compact">
                  Back to List
                </Link>
              )}
            </>
          ) : user.id === id && (
            <>
              <Link to={`/assets/new?employee_id=${id}`} className="btn btn-primary btn-compact">
                + Assign Asset
              </Link>
              <button
                className="btn btn-secondary btn-compact"
                onClick={() => setShowTimeAllocationModal(true)}
              >
                + Add Time Allocation
              </button>
              <Link to="/dashboard" className="btn btn-secondary btn-compact">
                Back to Dashboard
              </Link>
            </>
          )}
        </div>
      </div>

      <div className="card">
        <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
          {employee.picture ? (
            <img 
              src={employee.picture} 
              alt={employee.name}
              style={{ width: '120px', height: '120px', borderRadius: '50%', objectFit: 'cover' }}
            />
          ) : (
            <div style={{ 
              width: '120px', 
              height: '120px', 
              borderRadius: '50%', 
              backgroundColor: '#1976d2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '40px',
              fontWeight: 'bold'
            }}>
              {employee.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
            </div>
          )}
          
          <div style={{ flex: 1 }}>
            <h3 style={{ fontSize: '24px', marginBottom: '8px' }}>{employee.name}</h3>
            <p style={{ color: '#666', marginBottom: '16px' }}>{employee.email}</p>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
              {getStatusBadge(employee.status)}
              <span className="badge" style={{ backgroundColor: '#e3f2fd', color: '#1565c0' }}>
                {employee.role}
              </span>
            </div>
          </div>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '24px',
          marginTop: '32px',
          paddingTop: '24px',
          borderTop: '1px solid #e0e0e0'
        }}>
          <div>
            <label style={{ fontSize: '12px', color: '#666', textTransform: 'uppercase', fontWeight: '600' }}>
              Role
            </label>
            <p style={{ marginTop: '4px', fontSize: '16px' }}>
              {employee.role}
            </p>
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#666', textTransform: 'uppercase', fontWeight: '600' }}>
              Manager
            </label>
            <p style={{ marginTop: '4px', fontSize: '16px' }}>
              {employee.manager_name || 'Not assigned'}
            </p>
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#666', textTransform: 'uppercase', fontWeight: '600' }}>
              Status
            </label>
            <p style={{ marginTop: '4px', fontSize: '16px' }}>
              {getStatusBadge(employee.status)}
            </p>
          </div>
        </div>
      </div>

      {/* Assigned Assets Section */}
      <div className="card" style={{ marginTop: '24px' }}>
        <h3>Assigned Assets ({assets.length})</h3>
        {assets.length > 0 ? (
          <div className="table-container" style={{ marginTop: '16px' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Make / Model</th>
                  <th>Serial</th>
                  <th>Status</th>
                  <th>Assigned Date</th>
                </tr>
              </thead>
              <tbody>
                {assets.map(asset => (
                  <tr key={asset.id} onClick={() => navigate(`/assets/${asset.id}`)} style={{ cursor: 'pointer' }}>
                    <td>{asset.asset_type}</td>
                    <td>{asset.make} {asset.model}</td>
                    <td>{asset.serial_number || '-'}</td>
                    <td>{asset.status}</td>
                    <td>{asset.assigned_date ? new Date(asset.assigned_date).toLocaleDateString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p style={{ color: 'rgba(255,255,255,0.5)', marginTop: '16px' }}>
            No assets assigned to this employee.
          </p>
        )}
      </div>

      {/* Time Allocation History Section */}
      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3>Time Allocation History</h3>
          {user.id === id && (
            <button
              className="btn btn-primary btn-compact"
              onClick={() => setShowTimeAllocationModal(true)}
            >
              + Add Time Allocation
            </button>
          )}
        </div>
        {timeAllocationHistory.length === 0 ? (
          <p style={{ color: 'rgba(255,255,255,0.5)', marginTop: '16px' }}>
            No time allocation data recorded yet.
          </p>
        ) : (
          <div className="table-container" style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Week</th>
                  <th>Time Allocation</th>
                  <th>Notes</th>
                  {hasRole('hr_head', 'super_admin') && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {timeAllocationHistory.map(timeAllocation => (
                  <tr key={timeAllocation.id}>
                    <td>{formatWeekRange(timeAllocation.week_start_date)}</td>
                    <td>
                      <span className="badge" style={{
                        backgroundColor: timeAllocation.time_allocation_percentage === '75-100%' ? '#4caf50' :
                                       timeAllocation.time_allocation_percentage === '50-75%' ? '#2196f3' :
                                       timeAllocation.time_allocation_percentage === '25-50%' ? '#ff9800' : '#f44336',
                        color: '#fff'
                      }}>
                        {timeAllocation.time_allocation_percentage}
                      </span>
                    </td>
                    <td>{timeAllocation.notes || '-'}</td>
                    {hasRole('hr_head', 'super_admin') && (
                      <td>
                        <button
                          onClick={() => handleDeleteTimeAllocation(timeAllocation.id)}
                          className="btn-icon btn-delete"
                          title="Delete"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showTimeAllocationModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Add Weekly Time Allocation</h3>
              <button className="modal-close" onClick={() => setShowTimeAllocationModal(false)}>
                ×
              </button>
            </div>
            <form onSubmit={handleCreateTimeAllocation}>
              <div className="form-group">
                <label htmlFor="weekStartDate">Week *</label>
                <select
                  id="weekStartDate"
                  name="weekStartDate"
                  className="form-control"
                  value={timeAllocationFormData.weekStartDate}
                  onChange={handleTimeAllocationFormChange}
                  required
                >
                  <option value="">Select Week</option>
                  {weekOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="timeAllocationPercentage">Time Allocation Percentage *</label>
                <select
                  id="timeAllocationPercentage"
                  name="timeAllocationPercentage"
                  className="form-control"
                  value={timeAllocationFormData.timeAllocationPercentage}
                  onChange={handleTimeAllocationFormChange}
                  required
                >
                  <option value="">Select time allocation level</option>
                  <option value="0-25%">0-25% (Low utilization)</option>
                  <option value="25-50%">25-50% (Lightly loaded)</option>
                  <option value="50-75%">50-75% (Moderately loaded)</option>
                  <option value="75-100%">75-100% (Fully utilized)</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="notes">Notes</label>
                <textarea
                  id="notes"
                  name="notes"
                  className="form-control"
                  value={timeAllocationFormData.notes}
                  onChange={handleTimeAllocationFormChange}
                  rows="3"
                  placeholder="Optional notes about this week's time allocation..."
                />
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary btn-compact" onClick={() => setShowTimeAllocationModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary btn-compact">
                  Save Time Allocation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default EmployeeDetail;
